import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";
import { useLocation } from "react-router-dom";

type MinimalModeContextType = {
  minimalMode: boolean;
  setMinimalMode: (value: boolean) => void;
  toggleMinimalMode: () => void;
  exitMinimalMode: () => void;
};

const MinimalModeContext = createContext<MinimalModeContextType | undefined>(
  undefined
);

export const MinimalModeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [minimalMode, setMinimalMode] = useState(false);
  const location = useLocation();

  const toggleMinimalMode = useCallback(() => {
    setMinimalMode((prev) => !prev);
  }, []);

  const exitMinimalMode = useCallback(() => {
    setMinimalMode(false);
  }, []);

  // ESC key handler
  useEffect(() => {
    if (!minimalMode) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        exitMinimalMode();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [minimalMode, exitMinimalMode]);

  // Exit minimal mode on route change
  useEffect(() => {
    if (minimalMode) {
      setMinimalMode(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return (
    <MinimalModeContext.Provider
      value={{
        minimalMode,
        setMinimalMode,
        toggleMinimalMode,
        exitMinimalMode,
      }}
    >
      {children}
    </MinimalModeContext.Provider>
  );
};

export const useMinimalMode = () => {
  const context = useContext(MinimalModeContext);
  if (!context) {
    throw new Error("useMinimalMode must be used within a MinimalModeProvider");
  }
  return context;
};
